/**
 * 상세 페이지 (Concept A · Human / Warm).
 *
 * 시안:
 *   - 자료 풍부: wireframes/concept-a-warm/detail-rich.html (id ≈ 124)
 *   - 자료 부족: wireframes/concept-a-warm/detail-sparse.html (id ≈ 214) — 동일 컴포넌트, EmptyState 자동 활성화
 *
 * 섹션 순서:
 *   1. StickyHeader (뒤로 + 찜 + 공유)
 *   2. Hero (갤러리 첫 이미지 + 도트 인디케이터 + "1/N" 카운트)
 *   3. 자료 부족 안내 배너 (sparse 케이스에서만)
 *   4. 타이틀 블록 (eyebrow 위치 + h1 단지명 + 주소 + chip)
 *   5. 핵심 KV 6칸 (세대수 / 분양가="가격 문의" / 면적 / 입주 / 시공사 / 주차)
 *   6. 탭 sticky (개요 / 입지 / 평면도 / 갤러리 / 혜택 — 자료 있는 것만)
 *   7. 사업개요 (상세 설명 sanitized)
 *   8. 입지 (kakao 지도 + 주소)
 *   9. 평면도 (FloorplanScroller)
 *  10. 평형별 분양가 (모두 "가격 문의")
 *  11. 갤러리 (Gallery 2+1 모자이크 + lightbox)
 *  12. 혜택 (DiscountBenefit 카드)
 *  13. 사업주체 KV + 1533-8124 안내
 *  14. 하단 고정 InquiryCTA
 *
 * 정책 강제:
 *   - PriceTag 외 가격 표시 금지
 *   - 외부 홈페이지 링크 제거 (정책)
 *   - 모든 전화 액션은 INQUIRY_PHONE_TEL_LINK
 *   - 이모지 미사용 (SVG icon only)
 */

import { canContactProperty } from '../domain/inquiry/contactPolicy';
import { useAuth } from '../contexts/AuthContext';
import { BenefitAlertOptIn } from './property-detail/BenefitAlertOptIn';
import { propertyApi } from '../services/api';
import React, { useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSeoMeta, useHead } from '@unhead/react';
import { sanitizeHtml, removeContactInfo } from '../utils/sanitizeHtml';
import { formatMoveInDate } from '../utils/formatters';
import { formatUnits } from '../domain/property/formatters';
import { buildBadges } from '../domain/property/badges';
import { resolveContactPhone } from '../domain/inquiry/resolveContactPhone';
import { usePropertyDetail } from '../hooks/usePropertyDetail';
import { BENEFIT_INTENT_KEY, requestBenefit, type BenefitIntent } from '../design-system/components/section/BenefitSection';
import { useContactForm } from '../hooks/useContactForm';
import { propertyCategory, setTrackingProperty, trackBenefitUnlock } from '../utils/tracking';
import {
  InquiryCTA,
  Gallery,
  EmptyState,
  ContactStickyCard,
  DetailSkeleton,
  ErrorState,
  type GalleryImage as DSGalleryImage,
  type ContactStickyMiniRow,
} from '../design-system/components';
import { DetailLayout } from '../design-system/layouts';
import { stripEmoji } from '../domain/policy/content-rules';
import { buildImageUrl, extractEyebrow, formatAreaCell } from './property-detail/helpers';
import { PinIcon } from './property-detail/icons';
import { buildSeoData } from './property-detail/buildSeoData';
import { buildKvRows } from './property-detail/buildKvRows';
import { Hero } from './property-detail/Hero';
import { DetailStickyHeader } from './property-detail/DetailStickyHeader';
import { OfficialHomepageLink } from './property-detail/OfficialHomepageLink';
import { StickyAnchorNav, type AnchorItem } from './property-detail/StickyAnchorNav';
import { InquirySection } from './property-detail/InquirySection';
import { SideRecruitCard } from './property-detail/SideRecruitCard';
import { useNearbyPois } from './property-detail/useNearbyPois';
import { SectionRenderer, PoiList, SectionCard, type SectionDescriptor } from '../design-system/components/section';
import { CityIcon } from '../design-system/components/section/SectionIcons';
import { whenKakaoMapsReady } from '../utils/kakaoMapsReady';
import { addLocalRecentView } from '../utils/localRecentViews';
import {
  buildSpecCells,
  buildDealCompareRows,
  getPriceBasis,
  priceBasisLabel,
  orderDetailSections,
  dropDuplicatePriceSection,
  isDiscountPriceSection,
} from '../domain/property/detailV2';
import { parseLocationPoints, stripLocationPoints } from '../domain/property/locationPoints';
import { SpecBar } from './property-detail/SpecBar';
import { NearbySection } from './property-detail/NearbySection';
import {
  DealCompareSection,
  LocationPointsSection,
  DetailNotice,
} from './property-detail/DetailExtraSections';

// 구조화 섹션 key → 앵커 내비 id 매핑 (단지정보=basic → info).
const ANCHOR_ID_BY_KEY: Record<string, string> = {
  price: 'price',
  benefit: 'benefit',
  floorplan: 'floorplan',
  schedule: 'schedule',
  basic: 'info',
};

// ─────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────
const PropertyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    property,
    loading,
    loadError,
    reload,
    refresh,
    isFavorited,
    favoriteLoading,
    handleFavoriteToggle,
  } = usePropertyDetail(id);

  // ID → house_manage_no 리다이렉트 (기존 로직 유지)
  useEffect(() => {
    if (!property || !id) return;
    const isNumericId = /^\d+$/.test(id) && id.length < 10;
    // 받아 온 데이터가 지금 주소의 현장일 때만 — 이전 현장 주소로 되돌아가지 않게 (2026-10-05)
    if (isNumericId && String(property.id) === id && (property as any).house_manage_no) {
      navigate(`/property/${(property as any).house_manage_no}`, { replace: true });
    }
  }, [property, id, navigate]);

  // 익명 방문자 통계: 현장 조회 기록 (한 단지 mount 당 1회) - 2026-05-30
  useEffect(() => {
    if (!property?.id) return;
    import('../utils/visitorTracker').then((m) => m.trackPropertyView(property.id, 'detail_page'));
  }, [property?.id]);

  // 혜택 열람 리드 (2026-10-01) — 로그인 회원이 상담 가능 현장의 혜택을 보면 서버에 1회 기록.
  // 잠금 대상 여부·30일 중복은 서버가 판정하고, 여기서는 같은 탭에서 반복 호출만 막는다.
  const { isAuthenticated, user } = useAuth();
  const benefitCount = Array.isArray(property?.discount_benefits) ? property!.discount_benefits.length : 0;
  const contactable = property ? canContactProperty(property) : false;
  const benefitLeadInFlight = useRef<Set<string>>(new Set());   // 응답 오기 전 재실행(조용한 재조회 등)으로 두 번 보내지 않게
  useEffect(() => {
    if (!isAuthenticated || !property?.id || !contactable || benefitCount === 0) return;
    // 2026-10-05: 회원별 키 + 서버 성공 뒤에만 기록 — 실패하면 같은 탭에서 다시 시도, 계정을 바꾸면 그 회원도 기록 (Codex 검토)
    const key = `benefit_lead_${user?.id ?? 'me'}_${property.id}`;
    try {
      if (sessionStorage.getItem(key)) return;
    } catch {
      /* 저장소 불가 — 서버 30일 중복 방지에 맡긴다 */
    }
    if (benefitLeadInFlight.current.has(key)) return;
    benefitLeadInFlight.current.add(key);
    const pid = property.id;
    propertyApi
      .recordBenefitLead(pid)
      .then(() => {
        try {
          sessionStorage.setItem(key, '1');
        } catch {
          /* 무시 */
        }
        // 구글 광고 전환용 GA4 이벤트 — 서버 기록 성공 뒤, 같은 탭·회원·현장 1회 (2026-10-06)
        trackBenefitUnlock({ id: pid, name: property.title, category: propertyCategory(property.property_type) });
      })
      .catch(() => {})
      .finally(() => benefitLeadInFlight.current.delete(key));
  }, [isAuthenticated, user?.id, property?.id, contactable, benefitCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // 혜택 안내 요청(2026-10-08 대표 '추천안') — 잠금 현장 혜택은 그 현장 요청(상담 연락 동의) 뒤에 보인다.
  //  ① '이 현장 혜택 보기'(BenefitSection.requestBenefit)가 보내는 이벤트를 받아 화면을 비우지 않고 다시 받는다.
  //  ② 로그인 화면에서 이 현장 안내를 보고 로그인했으면(intent.ack) 돌아오자마자 자동 요청 — '카톡 로그인하면 보인다' 유지.
  const requestPending = Boolean(
    property?.discount_benefits &&
      !Array.isArray(property.discount_benefits) &&
      (property.discount_benefits as { request_required?: boolean }).request_required,
  );
  useEffect(() => {
    const onRequested = (e: Event) => {
      e.preventDefault();
      refresh();
    };
    window.addEventListener('voonang:benefit-requested', onRequested);
    return () => window.removeEventListener('voonang:benefit-requested', onRequested);
  }, [property?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const autoRequested = useRef<number | null>(null);
  useEffect(() => {
    if (!isAuthenticated || !property?.id || !requestPending || autoRequested.current === property.id) return;
    let intent: BenefitIntent | null = null;
    try {
      intent = JSON.parse(localStorage.getItem(BENEFIT_INTENT_KEY) || 'null');
    } catch {
      intent = null;
    }
    if (!intent || !intent.ack || intent.id !== property.id) return;
    autoRequested.current = property.id;
    try {
      localStorage.removeItem(BENEFIT_INTENT_KEY);
    } catch {
      /* 무시 */
    }
    requestBenefit(property.id);
  }, [isAuthenticated, property?.id, requestPending]);

  // 비로그인 마이 '최근 본 단지'(이 기기) — v2 S4 (2026-09-22 v2h)
  useEffect(() => {
    if (!property?.id) return;
    addLocalRecentView({
      id: property.id,
      house_manage_no: (property as any).house_manage_no ?? null,
      title: property.title,
      address: property.address ?? null,
      min_price: property.min_price ?? null,
      max_price: property.max_price ?? null,
      image: property.image_thumbnail_url || property.image_card_url || property.image_url || null,
    });
  }, [property?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hero 이미지 + 갤러리 통합 (gallery_images 우선, 없으면 image_url)
  const galleryImages: DSGalleryImage[] = useMemo(() => {
    if (!property) return [];
    const list: DSGalleryImage[] = [];
    // 대표 이미지가 있으면 첫 번째
    const heroSrc = buildImageUrl(property.image_url);
    if (heroSrc) list.push({ src: heroSrc, alt: property.title });
    // 갤러리 인라인
    ((property as any).gallery_images ?? []).forEach((g: any) => {
      const src = buildImageUrl(g.image_medium || g.image);
      if (src) list.push({ src, alt: g.caption ?? property.title });
    });
    // 섹션 이미지들도 갤러리 fallback에 추가 (자료 풍부 시)
    const sectionKeys = [
      'exterior_image', 'complex_layout_image', 'business_overview_image',
      'brand_intro_image', 'location_image', 'premium_image',
      'community_image', 'specialization_image', 'design_image',
      'landscape_image', 'system_image',
    ];
    sectionKeys.forEach((k) => {
      const src = buildImageUrl((property as any)[k]);
      if (src && !list.find((it) => it.src === src)) {
        list.push({ src, alt: property.title });
      }
    });
    return list;
  }, [property]);

  // Sparse 감지: 이미지 0장 + 면적·세대수·입주일 중 2개 이상 비어있음
  const isSparse = useMemo(() => {
    if (!property) return false;
    const hasImg = galleryImages.length > 0;
    const hasUnits = !!(property.total_units && property.total_units > 0);
    const hasArea = !!((property.min_area || 0) > 0 || (property.max_area || 0) > 0);
    const hasMoveIn = !!(property as any).move_in_date;
    const missing = [hasImg, hasUnits, hasArea, hasMoveIn].filter((v) => !v).length;
    return missing >= 2;
  }, [property, galleryImages]);

  // 구조화 섹션 스택 — 백엔드 property.sections 를 SectionRenderer 로 통일 렌더.
  // summary 는 TitleBlock 이 담당하므로 제외. location(auto) 은 아래 Kakao 지도 섹션이
  // 담당하므로 제외(플레이스홀더 중복·지도 회귀 방지). 나머지 auto/custom 을 order 대로.
  const infoSections: SectionDescriptor[] = useMemo(() => {
    const list = ((property as any)?.sections ?? []) as SectionDescriptor[];
    return list.filter(
      (s) => !(s.kind === 'auto' && (s.key === 'summary' || s.key === 'location')),
    );
  }, [property]);

  // Kakao 지도 이니셜라이즈 — SDK(autoload=false) 초기화 완료를 기다린 뒤 생성.
  // 직접 진입 시 'kakao.maps.LatLng is not a constructor' 로 ErrorBoundary 전체 오류가 나던
  // 경쟁 조건 수정 (2026-09-21 v2b). SDK 가 끝내 준비되지 않으면 지도만 생략(throw 없음).
  const mapDivRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!property || !property.latitude || !property.longitude) return;
    let cancelled = false;
    const lat = property.latitude;
    const lng = property.longitude;
    whenKakaoMapsReady().then((kakao) => {
      if (cancelled || !kakao || !mapDivRef.current) return;
      try {
        const map = new kakao.maps.Map(mapDivRef.current, {
          center: new kakao.maps.LatLng(lat, lng),
          level: 4,
        });
        new kakao.maps.Marker({ position: new kakao.maps.LatLng(lat, lng), map });
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.warn('[detail map] init skipped', e);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [property]);

  // 주변 POI(지하철·학교·마트·병원) — 카카오 카테고리 검색. 실패 시 빈 배열 폴백.
  const { items: poiItems } = useNearbyPois(property?.latitude, property?.longitude);

  // SEO data — 운영 페이지 voonang.com/property/2749 와 동일 형태 (사용자 피드백 2026-05-19)
  const seoData = useMemo(() => buildSeoData(property), [property]);

  // ─────────────────────────────────────────────
  // SEO 메타 주입 (@unhead/react, React 19 호환)
  // - seoData 가 nullable 이므로 early return 전에 hook 호출 (rules-of-hooks)
  // - useSeoMeta: 표준 SEO 메타 / useHead: 비표준 메타 + canonical/preload + JSON-LD 5종
  // ─────────────────────────────────────────────
  useSeoMeta(
    seoData
      ? {
          title: seoData.title,
          description: seoData.description,
          author: '부냥',
          generator: '부냥 분양정보 플랫폼',
          robots: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
          appleMobileWebAppTitle: property?.title,
          ogType: 'website',
          ogTitle: seoData.title,
          ogDescription: seoData.ogDescription,
          ogUrl: seoData.url,
          ogImage: seoData.image,
          ogSiteName: '부냥',
          ogLocale: 'ko_KR',
          twitterCard: 'summary_large_image',
          twitterSite: '@voonang',
          twitterTitle: seoData.title,
          twitterDescription: seoData.description,
          twitterImage: seoData.image,
        }
      : {},
  );

  // useHead: useSeoMeta 가 직접 지원하지 않는 비표준 메타(keywords/NaverBot/naverbot:*/place:*)와
  // canonical link / LCP preload / JSON-LD 5종 주입. unhead 의 strict 한 타입을 우회하기 위해
  // 인자 객체를 어셔션으로 전달 (런타임 결과는 동일).
  useHead(
    seoData
      ? ({
          meta: [
            { name: 'keywords', content: seoData.keywords },
            { name: 'googlebot', content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1' },
            { name: 'NaverBot', content: 'index, follow' },
            { name: 'Yeti', content: 'index, follow' },
            { property: 'og:image:width', content: '1200' },
            { property: 'og:image:height', content: '630' },
            { property: 'naverbot:title', content: seoData.title },
            { property: 'naverbot:description', content: seoData.description },
            ...(property?.latitude != null
              ? [{ property: 'place:location:latitude', content: String(property.latitude) }]
              : []),
            ...(property?.longitude != null
              ? [{ property: 'place:location:longitude', content: String(property.longitude) }]
              : []),
          ],
          link: [
            { rel: 'canonical', href: seoData.url },
            ...(seoData.image ? [{ rel: 'preload', as: 'image', href: seoData.image }] : []),
          ],
          script: seoData.jsonLd.map((item) => ({
            type: 'application/ld+json',
            innerHTML: JSON.stringify(item),
          })),
        } as Parameters<typeof useHead>[0])
      : {},
  );

  // 컨택트폼 (이름 + 010-XXXX-XXXX) — 운영 페이지 "상담 신청" 동등
  // useContactForm 훅: POST /api/properties/{id}/contact/ + 저장 성공 시 광고 전환 Lead (utils/tracking)
  const { contactForm, setContactForm, formSubmitting, handleContactSubmit, submittedPhone } = useContactForm(
    property?.id,
    property?.title ?? '',
    property?.property_type,
  );

  // 이 화면의 전화·카톡 클릭 Contact 이벤트 대상 현장 (이탈 시 목록 공통값으로 복귀)
  useEffect(() => {
    if (!property) return undefined;
    setTrackingProperty({ id: property.id, name: property.title, category: propertyCategory(property.property_type) });
    return () => setTrackingProperty(null);
  }, [property]);

  // 로딩
  // 로딩 — v2 S1 스켈레톤 (스피너 금지)
  if (loading) {
    return (
      <div className="min-h-screen bg-paper">
        <div className="mx-auto max-w-[480px] bg-frame md:max-w-[720px] lg:max-w-[1140px]">
          <DetailSkeleton />
        </div>
      </div>
    );
  }

  // 통신 에러 — v2 S3 (다시 시도 + 전화 + 오류코드·시각). 404 는 아래 '찾을 수 없음'
  if (!property && loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper">
        <ErrorState onRetry={reload} code={loadError.code} at={loadError.at} />
      </div>
    );
  }

  // 404
  if (!property || !seoData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper px-4">
        <EmptyState
          title="분양 현장을 찾을 수 없어요"
          description="요청하신 현장 정보가 존재하지 않거나 삭제되었습니다."
          actionLabel="홈으로 돌아가기"
          onAction={() => navigate('/')}
        />
      </div>
    );
  }

  const eyebrow = extractEyebrow(property.address);
  // 상세설명: detailed_description 우선, 없으면 description (운영 페이지 동일 흐름)
  const rawDescription = property.detailed_description || (property as any).description || '';
  // 입지 4카드: [교통]/[학군]/[생활]/[호재] 줄 파싱 (대표 결정 #3). 본문에서는 표식 줄 제거.
  const locationPoints = parseLocationPoints(rawDescription);
  const descriptionBody = locationPoints.length > 0 ? stripLocationPoints(rawDescription) : rawDescription;
  const sanitizedDetail = descriptionBody
    ? sanitizeHtml(stripEmoji(removeContactInfo(descriptionBody)), { convertImageUrls: true })
    : '';
  const priceBasis = getPriceBasis(property);
  const specCells = buildSpecCells(property);
  // PC 스펙바 5번째 칸 '주차' (목업 PC 04) — 값 있을 때만
  const parkingSpaces = Number((property as any).parking_spaces) || 0;
  const pcSpecCells = parkingSpaces > 0
    ? [{
        key: 'parking',
        label: '주차',
        value: `${parkingSpaces.toLocaleString()}대`,
        sub: property.total_units && property.total_units > 0 ? `세대당 ${(parkingSpaces / property.total_units).toFixed(1)}` : undefined,
      }]
    : [];
  const dealRows = buildDealCompareRows(property);
  // 대표 지시 2026-09-22: '할인 분양가 안내' 류 커스텀 섹션이 있으면 자동 '타입별 분양가'는 중복이라 뺀다.
  // 정렬 뒤에 거른다(커스텀 섹션이 분양가 자리를 유지). priceBasis 는 원본 sections 기준 그대로.
  const orderedSections = orderDetailSections(infoSections);
  const primarySections = dropDuplicatePriceSection(orderedSections.primary, infoSections);
  const restSections = dropDuplicatePriceSection(orderedSections.rest, infoSections);
  // 자동 분양가가 빠진 경우 '분양가' 앵커는 첫 할인 분양가 커스텀 섹션으로 연결
  const discountPriceSection = infoSections.find(isDiscountPriceSection);

  // 기본단지정보 표 (사용자 피드백 2026-05-21 / 벤치마크 reby24.com/sale/585):
  // 모든 메타데이터를 단일 표로. 분양가는 표의 마지막 행에 강조 색상 (accent) 으로 배치.
  const kvConfirmed = buildKvRows(property);

  // 확인 / 확인중 분리 (sparse 케이스)
  const confirmedRows = kvConfirmed.filter((r) => !r.pending);
  const pendingRows = kvConfirmed.filter((r) => r.pending);

  // v2 전화 규칙 (대표 결정 5): 사이드문의=1533-8124, 담당자 현장=contact_number → model_house_phone → 1533-8124
  const contactPhone = resolveContactPhone(property);

  // ContactStickyCard (lg+ 우측)용 miniRows
  // rich: 핵심 4행, sparse: 확인된 + 확인중 chip
  const contactMiniRows: ContactStickyMiniRow[] = isSparse
    ? [
        ...confirmedRows.slice(0, 2).map((r) => ({ key: r.key, value: r.value })),
        ...pendingRows.slice(0, 2).map((r) => ({ key: r.key, value: null, pending: true })),
      ]
    : [
        ...(property.total_units && property.total_units > 0
          ? [{ key: '세대수', value: formatUnits(property, { short: true }) }]
          : []),
        ...(property.min_area || property.max_area
          ? [{ key: '전용', value: formatAreaCell(property.min_area, property.max_area) }]
          : []),
        ...((property as any).move_in_date
          ? [{ key: '입주', value: formatMoveInDate((property as any).move_in_date) || String((property as any).move_in_date) }]
          : []),
        ...(property.constructor ? [{ key: '시공', value: property.constructor }] : []),
      ].slice(0, 4);

  // 앵커 내비 항목 — DOM 순서대로 (개요 → 분양가/혜택/단지정보 → 상세설명 → 위치).
  // 존재하는 섹션만 노출한다.
  const autoKeys = new Set(
    infoSections.filter((s) => s.kind === 'auto').map((s) => s.key),
  );
  if (discountPriceSection) autoKeys.add('price'); // 앵커 '분양가' → 할인 분양가 안내
  const anchors: AnchorItem[] = [{ id: 'overview', label: '개요' }];
  if (autoKeys.has('benefit')) anchors.push({ id: 'benefit', label: '혜택' });
  if (autoKeys.has('price')) anchors.push({ id: 'price', label: '분양가' });
  if (autoKeys.has('floorplan')) anchors.push({ id: 'floorplan', label: '평면도' });
  if (autoKeys.has('schedule')) anchors.push({ id: 'schedule', label: '일정' });
  if (property.latitude && property.longitude) anchors.push({ id: 'location', label: '입지' });
  if (autoKeys.has('basic')) anchors.push({ id: 'info', label: '단지정보' });
  if (sanitizedDetail) anchors.push({ id: 'description', label: '상세설명' });

  const renderSection = (s: SectionDescriptor) => {
    const anchorId =
      s.kind === 'auto' ? ANCHOR_ID_BY_KEY[s.key] : s === discountPriceSection ? ANCHOR_ID_BY_KEY.price : undefined;
    return (
      <div
        key={(s as any).id ?? `${s.kind}-${s.key}`}
        id={anchorId}
        className={anchorId ? 'scroll-mt-[100px]' : undefined}
      >
        <SectionRenderer section={s} property={property} variant="detail" />
        {/* 혜택을 본 바로 그 자리에서 마케팅 수신 동의를 한 번만 제안 (2026-10-05) */}
        {s.kind === 'auto' && s.key === 'benefit' && isAuthenticated && contactable && benefitCount > 0 && (
          <BenefitAlertOptIn propertyTitle={property.title} />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-paper">
      <div className="mx-auto max-w-[480px] bg-frame md:max-w-[720px] lg:max-w-[1140px] xl:max-w-[1240px] 2xl:max-w-[1320px]">
        {/* 1. StickyHeader — md+ DesktopNav, lg+ DesktopPhonePill */}
        <DetailStickyHeader
          property={property}
          seoData={seoData}
          onBack={() => navigate(-1)}
          isFavorited={isFavorited}
          favoriteLoading={favoriteLoading}
          onFavoriteToggle={handleFavoriteToggle}
        />

        {/* 2. Hero — v2h(2026-09-22) 목업 04: 전체 폭, 단지명·주소 오버레이 (구 TitleBlock 대체) */}
        <div id="overview" className="scroll-mt-[100px]">
          <Hero
            images={galleryImages}
            searchVolume={property.naver_search_volume}
            totalCount={galleryImages.length}
            badges={buildBadges(property)}
            hugGuarantee={Boolean(property.hug_guarantee)}
            title={property.title}
            address={property.address}
            eyebrow={property.constructor ? `${property.constructor} 시공` : eyebrow}
            soldOut={(property.status ?? '').trim() === '분양완료'}
          />
        </div>

        {/* 본문 + 우측 sticky 카드 — lg+ DetailLayout 분할.
            사용자 피드백 2026-05-22:
              - 일반 현장: 풀 카드 (단지명·분양가·미니KV·전화/카톡 액션)
              - 사이드 현장: 기본 레이아웃 유지 + hideContact=true 로 전화번호·전화/카톡 버튼만 숨김 */}
        <DetailLayout
          aside={
            <ContactStickyCard
              variant={isSparse ? 'sparse' : 'rich'}
              propertyTitle={property.title}
              miniRows={contactMiniRows}
              minPrice={property.min_price}
              maxPrice={property.max_price}
              priceLabel={priceBasisLabel(priceBasis)}
              priceSub={property.price_per_pyeong && property.price_per_pyeong > 0 ? `평당 ${property.price_per_pyeong.toLocaleString()}만` : undefined}
              hideContact={!canContactProperty(property)}
              phone={contactPhone}
            />
          }
          main={
            <>
        {/* 3. 스펙바 (v2 .metrics — 규모·전용·분양가(원분양가/할인가)·입주, PC 는 주차 추가) — 모바일은 히어로에 겹침 */}
        <SpecBar cells={specCells} pcCells={pcSpecCells} overlap />

        {/* 4. Sparse 안내 배너 */}
        {isSparse && (
          <EmptyState
            variant="banner"
            title="자료를 추가로 받아오고 있어요"
            description="시행사가 자료를 보내주는 대로 평면도와 사진을 업데이트할게요."
          />
        )}

        {/* 5-0. 공식 홈페이지 — 자사 랜딩(is_custom_homepage)만 (기존 유지) */}
        <OfficialHomepageLink property={property} />

        {/* 스티키 앵커 내비 (목업 `.anav`) */}
        <StickyAnchorNav items={anchors} />

        {/* 5. v2 섹션 스택 — 혜택안내 → 타입별 분양가 → 평면도 → 청약일정 → 실거래 비교 → 입지 → 모델하우스
            → 단지정보·관리자 커스텀 → 홍보자료 → 상세설명 → 인근 단지. (DB 섹션은 orderDetailSections 로 v2 순서,
            커스텀 섹션은 DB 상 앞 섹션에 붙어 이동) */}
        <div className="flex flex-col gap-2.5 bg-paper px-2.5 py-2.5">
          {primarySections.map(renderSection)}

          <DealCompareSection rows={dealRows} />

          {/* 입지 — 4카드(파싱) + 카카오 지도 + 주변 POI */}
          <LocationPointsSection points={locationPoints} />
          {property.latitude && property.longitude && (
            <div id="location" className="scroll-mt-[100px]">
              <SectionCard title="위치 · 주변" icon={<CityIcon />}>
                <div
                  ref={mapDivRef}
                  className="aspect-[16/10] w-full overflow-hidden rounded-ca-xl bg-block"
                />
                {property.address && (
                  <p className="mt-3 flex items-center gap-1.5 text-[12.5px] text-muted">
                    <PinIcon /> {property.address}
                  </p>
                )}
                {poiItems.length > 0 && <PoiList items={poiItems} className="mt-3" />}
              </SectionCard>
            </div>
          )}

          {/* 모델하우스(견본주택) 주소는 상세에 표시하지 않는다 — 대표 지시 2026-09-22, 앞으로도 유지 */}

          {restSections.map(renderSection)}

          {/* 홍보자료 (기존 갤러리 — 보존) */}
          {galleryImages.length > 0 && (
            <div id="gallery" className="scroll-mt-[100px]">
              <SectionCard title="홍보자료">
                <Gallery images={galleryImages} maxPreview={3} sparse={isSparse} layout="responsive" />
              </SectionCard>
            </div>
          )}

          {/* 상세설명 */}
          {sanitizedDetail && (
            <div id="description" className="scroll-mt-[100px]">
              <SectionCard title="상세설명">
                <div
                  className="prose-detail text-[13.5px] leading-[1.9] text-body [&_p]:mb-2.5 [&_p:last-child]:mb-0 [&_strong]:font-bold [&_strong]:text-ink [&_h3]:mt-4 [&_h3]:mb-1.5 [&_h3]:text-[14.5px] [&_h3]:font-bold [&_h3]:text-ink [&_h4]:mt-3 [&_h4]:mb-1 [&_h4]:text-[13.5px] [&_h4]:font-bold [&_h4]:text-ink [&_br+br]:block [&_br+br]:content-[''] [&_br+br]:h-2"
                  dangerouslySetInnerHTML={{ __html: sanitizedDetail }}
                />
              </SectionCard>
            </div>
          )}

          {/* 인근 단지 (지도 경량 API 캐시 · 내부 링크만) */}
          <NearbySection property={property} />
        </div>

        {/* 고지 */}
        <DetailNotice />

        {/* 6. 분양 문의 — 컨택트폼 (운영 voonang.com/property/2749 동등)
            useContactForm: POST /api/properties/{id}/contact/ + Meta Pixel 추적.
            2026-10-05 대표: 담당자 없는 현장도 폼은 둔다 — 서버가 '사이드 문의'로 접수(메일 [사이드 문의]·고객 알림톡 '상담사 배정 후 연락').
            전화·카톡은 담당자 있는 현장만(phone 을 넘기지 않으면 접수 완료 화면에도 통화 안내가 없다). */}
        {/* 2026-10-06 대표: 담당자 없는 현장은 고객 폼을 숨기고 '사이드 문의'(분양 상담사용) 버튼만 —
            고객이 문의 남기는 곳으로 착각하지 않게. 관심고객 사전등록 현장은 예외(고객 모집용 폼). */}
        {canContactProperty(property) || property.is_pre_registration ? (
          <InquirySection
            property={property}
            contactForm={contactForm}
            setContactForm={setContactForm}
            formSubmitting={formSubmitting}
            handleContactSubmit={handleContactSubmit}
            submittedPhone={submittedPhone}
            phone={canContactProperty(property) ? contactPhone : null}
          />
        ) : (
          <SideRecruitCard property={property} />
        )}
            </>
          }
        />

        {/* 컨택트폼 하단 sticky InquiryCTA(약 70px) 가림 회피.
            BottomNavigation(58px) 은 App.tsx <main> 의 pb-[58px] 가 일괄 처리. */}
        {canContactProperty(property) && <div aria-hidden className="h-[78px] lg:hidden" />}

        {/* 13. 하단 고정 CTA — 사용자 피드백 2026-05-22: 사이드 현장은 숨김 */}
        {canContactProperty(property) && (
          <InquiryCTA
            variant="sticky"
            phone={contactPhone}
            onFavoriteToggle={handleFavoriteToggle}
            isFavorited={isFavorited}
            favoriteCount={property.favorite_count}
          />
        )}
      </div>
    </div>
  );
};

export default PropertyDetailPage;
